import { EventDispatcher, copyToClipboard, deepCopy, gebi, getTimeText, isShowresultPage, l, lLink, lreplace, qs, shareClick, storedItemCreate, storedItemDelete, storedItemRead } from '@utils/toolbox';
import { compareTable, floatingCol } from './templates';
import { ConnectApi } from '@js/plugins/connectApi';
import { Dialog } from '@js/components/dialog';
import { Drawer } from '@js/components/drawer';
import { cabinType } from '@components/resultsRenderHelpers';
import { resultsRender } from '@components/resultsRender';
import { toaster } from '@js/components/toast';


/**
	Favorite:

    This object will hold a list of flights to that are favorite and save them to local storage.
    It has two modes:
    > 'drawer': It will display a drawer with the hold flights and a CTA to go to the favorite page.
	> 'list': It will display the classic list from the showresults page, with small updates.
    > 'table': It will return an html component that can be used as compared list/tableIn the favorite page,
        it will display the flights to be compared and handle all user interactions.
**/


export let favoriteSettings = [
	{ id: 'price', text: l('flightcompare.setting.price'), display: 'price', min: '99999', visible: true },
	{ id: 'rebooking', text: l('showresult.filter.flexTicket'), display: 'check', visible: true },
	{ id: 'baggage', text: l('global.simplePhrases.baggageIncluded'), display: 'check', visible: true },
	{ id: 'cabinBaggage', text: l('global.simplePhrases.cabinBaggageIncluded'), display: 'check', visible: true },
	{ id: 'climateCompensated', text: l('showresult.filter.showClimateCompensationShort'), display: 'check', visible: true },
	{ id: 'sameDayArrival', text: l('flightcompare.setting.sameDayArrival'), display: 'check', visible: true },
	{ id: 'tripType', text: l('searchBox.tripType.label'), display: 'value', visible: true },
	{ id: 'stops', text: l('flightcompare.setting.stops'), display: 'value', visible: true },
	{ id: 'airlines', text: l('flightcompare.setting.airlines'), display: 'value', visible: true },
	{ id: 'climateCompensationPrice', text: l('flightcompare.setting.climateCompensationPrice'), display: 'price', visible: false },
	{ id: 'carbonEmisionValue', text: l('flightcompare.setting.carbonEmisionValue'), display: 'value', visible: true },
	{ id: 'passengers', text: l('flightcompare.setting.passengers'), display: 'value', visible: true },
	{ id: 'travelTime', text: l('flightcompare.setting.travelTime'), display: 'value', visible: true },
	{ id: 'waitingTimesOutbound', text: l('flightcompare.setting.outbound.waitingTime'), display: 'value', visible: true },
	{ id: 'outboundDepartureDate', text: l('flightcompare.setting.outbound.departureDate'), display: 'value', visible: true },
	{ id: 'outboundDepartureTime', text: l('flightcompare.setting.outbound.departureTime'), display: 'value', visible: true },
	{ id: 'outboundArrivalDate', text: l('flightcompare.setting.outbound.arrivalDate'), display: 'value', visible: true },
	{ id: 'outboundArrivalTime', text: l('flightcompare.setting.outbound.arrivalTime'), display: 'value', visible: true },
	{ id: 'outboundTrips', text: l('flightcompare.setting.outbound.trips'), display: 'accordion', children: [], visible: true },
	{ id: 'outboundDestinationAirport', text: l('flightcompare.setting.outbound.destination'), display: 'value', visible: true },
	{ id: 'waitingTimesInbound', text: l('flightcompare.setting.inbound.waitingTime'), display: 'value', visible: true },
	{ id: 'inboundDepartureDate', text: l('flightcompare.setting.inbound.departureDate'), display: 'value', visible: true },
	{ id: 'inboundDepartureTime', text: l('flightcompare.setting.inbound.departureTime'), display: 'value', visible: true },
	{ id: 'inboundArrivalDate', text: l('flightcompare.setting.inbound.arrivalDate'), display: 'value', visible: true },
	{ id: 'inboundArrivalTime', text: l('flightcompare.setting.inbound.arrivalTime'), display: 'value', visible: true },
	{ id: 'inboundTrips', text: l('flightcompare.setting.inbound.trips'), display: 'accordion', children: [], visible: true },
	{ id: 'inboundDestinationAirport', text: l('flightcompare.setting.inbound.destination'), display: 'value', visible: true }
];

export function favoriteSettingsFilteredByTableItems (tableItems) {
	// If none of the table items have an entry with the following extras then hide the setting
	return favoriteSettings.filter((setting) => {
		if (!setting.visible) {
			return false;
		}
		if (['rebooking', 'baggage', 'cabinBaggage', 'climateCompensated', 'stops', 'waitingTimesOutbound', 'waitingTimesInbound'].includes(setting.id)) {
			return tableItems.some(item => item.extra[setting.id]);
		}
		return true;
	});
}

export class Favorite {
	/**
	 *
	 * @param {string} mode - drawer, list, table
	 * @param {string} pageUrl - url to the favorite page
	 * @param {string|HTMLElement} parentElement - parent element to render the favorite
	 */
	constructor (mode = 'drawer', pageUrl = '/' + lLink('global.navigation.favoriteLink'), parentElement) {
		this.isShowresultPage = isShowresultPage();
		this.mode = mode;
		this.pinnedIndex = false;
		this.pageUrl = document.location.origin + pageUrl;
		this.favouriteArray = [];
		this.shareModeEnabled = false;
		this.processedFlights = [];
		this.parentElement = parentElement ? parentElement : qs('main section');
		resultsRender.init();

		window.favoriteComponent = this;

		const url = new URL(window.location.href);

		if (url.searchParams.get('share') || this.sharedKeyGet()) {
			this.sharedKeySet(url.searchParams.get('share'));
			this.shareModeEnabled = true;

			EventDispatcher.addEventListener('sharedFetched', (response) => {
				if (response) {
					this.favouriteArray = response?.flights || [];
					this.sharedNameSet(response.name);
					this.render();
				}
			});

			EventDispatcher.addEventListener('sharedItemAdded', (response) => {
				if (response.status === 'ok') {
					this.favouriteArray = response?.data?.flights || [];
				}
			});

			EventDispatcher.addEventListener('sharedRemoved', (response) => {
				if (response.status === 'ok') {
					this.favouriteArray = response?.data?.flights || [];
				}
			});

			EventDispatcher.addEventListener('sharedRefreshed', (response) => {
				if (response.status === 'ok') {
					this.favouriteArray = response?.data?.flights || [];
				}
			});

			this.pageUrl += '?share=' + this.sharedKeyGet();

			this.sharedListGet();
		} else {
			this.localStorageRead();
			this.render();
		}
	}


	/**
	 *
	 * @param {number} responseId - Response ID. Used to generate the uid.
	 * @param {number} orgIndex - orgIndex of the flight as returned from the backend. Used to generate the uid.
	 * @param {object} hashes - Hashes object. Used when refreshing and rendering a flight.
	 * @param {object} filters - Filters object. Used to filter the flights and for rendering.
	 * @param {object} urlOptions - Url options. Used to generate the uid. For example  { flexTicket: 1, baggageIncluded: 1 }.
	 */
	itemCreate (responseId, orgIndex, filters, hashes, urlOptions = {}) {
		if (!responseId || typeof orgIndex !== 'number' || !urlOptions) {
			console.error('createFavouriteObject: Faulty parameters', { responseId, orgIndex, urlOptions });
			throw new Error('createFavouriteObject: Faulty parameters');
		}

		const favouriteObject = {
			hashes,
			urlOptions,
			filters,
			orgIndex,
			responseId,
			uid: this.itemGenerateUid(responseId, orgIndex, urlOptions)
		};

		if (import.meta.env.DEV) {
			console.debug('createFavouriteObject', favouriteObject);
		}

		if (this.shareModeEnabled) {
			this.sharedItemAdd(favouriteObject);
		} else {
			this.favouriteArray.push(favouriteObject);
			this.localStorageWrite();
		}

		this.drawerManager('add');
	}

	/**
	 *
	 * @param {object} orgIndex - orgIndex of the flight
	 * @param {number} responseId - Response ID
	 * @param {object} urlOptions - Url options, for example { flexTicket: 1, baggageIncluded: 1 }
	 * @returns
	 */
	itemGenerateUid (responseId, orgIndex, urlOptions) {
		return `${responseId}-${orgIndex}-${JSON.stringify(urlOptions || {})}`;
	}

	/**
	 * @param {string} uid - unique id of the flight
	 * @returns {number} index of the flight in the array, -1 if not found
	 */
	itemGetIndexByUid (uid) {
		if (!this.favouriteArray?.length) {
			return -1;
		}
		return this.favouriteArray.findIndex(flight => flight.uid === uid);
	}

	/**
	 * Removes a flight from the array based on the index or the hashes
	 * @param {number} flightIndex
	 * @param {boolean} showDialog
	 * @returns
	 */
	itemRemoveByIndex (flightIndex, showDialog = true) {
		if (flightIndex === -1) {
			if (import.meta.env.DEV) {
				console.error('removeFavouriteObject: Invalid flight index', flightIndex);
			}
			toaster.newToast(l('fav.ui.removeError'), 'error', 'top');
			return;
		}

		const removeFlightActions = () => {
			const item = deepCopy(this.favouriteArray[flightIndex]);
			this.favouriteArray.splice(flightIndex, 1);
			if (!this.isShowresultPage) {
				resultsRender.response.flights.splice(flightIndex, 1);
			}
			if (this.pinnedIndex > flightIndex) {
				this.pinnedIndex--;
			}
			if (this.shareModeEnabled) {
				this.sharedItemRemove(item);
			} else {
				if (!this.favouriteArray.length && this.mode === 'drawer') {
					this.drawerManager('destroy');
				}
				this.localStorageWrite();
			}
		};

		const updateUI = () => {
			if (showDialog) {
				this.render();
			} else {
				this.drawerManager('remove');
			}
		};

		if (showDialog) {
			const dialog = new Dialog(l('fav.ui.removeConfirmation'), '', () => {
				removeFlightActions();
				updateUI();
			}, { cancelOnClickout: true });
			dialog.show();
		} else {
			removeFlightActions();
			updateUI();
		}
	}

	/**
	 * If -1 is passed, it will unpin the flight
	 * @param {number} flightIndex
	 */
	itemPinByIndex (flightIndex = -1) {
		this.pinnedIndex = flightIndex;
		this.render();
	}

	async itemUpdateByIndex (flightIndex, element) {
		const { responseId, uid } = this.favouriteArray[flightIndex];

		const newFavouriteItem = deepCopy(this.favouriteArray[flightIndex]);

		let colInfo = element.querySelector('.colInfo');

		if (!colInfo) {
			element.insertAdjacentHTML('beforeend', `<div class="colInfo"></div>`);
			colInfo = element.querySelector('.colInfo');
		}

		colInfo.innerHTML = '';

		const colInfoAxis = this.mode === 'list' ? 'width' : 'height';

		const updatePercent = (percent) => {
			colInfo.dataset.percentage = percent + '%';
			colInfo.style[colInfoAxis] = percent + '%';
		};

		const breakRecord = (message) => {
			element.dataset.status = 'broken';
			colInfo.dataset.percentage = '';
			colInfo.innerHTML = '<span>' + message + '<span class="icon icon-delete" data-action="delete"></span></span>';
		};

		const initSearch = () => {
			element.dataset.status = 'inactive';
			colInfo.classList.add('refreshing');
			return ConnectApi('Search', 'mimicSearchById', [responseId]);
		};

		const updateSearch = (reference) => {
			return new Promise((resolve, reject) => {
				const updateInterval = setInterval(async () => {
					const data = await ConnectApi('Search', 'update', [reference.results]).catch(reject);
					if (data?.results?.status === 'searching') {
						updatePercent(data.results.percentage);
					} else if (data?.results?.status === 'complete') {
						newFavouriteItem.responseId = data.id;
						updatePercent(100);
						clearInterval(updateInterval);
						resolve(data.results);
					}
				}, 1000);
			});
		};

		const getRefreshed = (data) => {
			colInfo.classList.remove('refreshing');
			return ConnectApi('Favorite', 'getFlights', [[newFavouriteItem], true]);
		};

		const handleRefreshedFlight = (response) => {
			if (!response.flights.length) {
				breakRecord(l('fav.ui.refreshNotFound'));
				return;
			}

			// Merge new meta data with existing
			resultsRender.response = {
				...resultsRender.response,
				airlineDetails: {
					...resultsRender.response?.airlineDetails,
					...response?.airlineDetails
				},
				airportDetails: {
					...resultsRender.response?.airportDetails,
					...response?.airportDetails
				},
				equipment: {
					...resultsRender.response?.equipment,
					...response?.equipment
				},
				segments: {
					...resultsRender.response?.segments,
					...response?.segments
				},
				trips: {
					...resultsRender.response?.trips,
					...response?.trips
				}
			};

			// Update flights with new data
			resultsRender.response.flights[flightIndex] = response.flights[0];
			resultsRender.response.flights[flightIndex].favouriteMeta.isChanging = true;

			element.classList.remove('oldResult');
			colInfo.dataset.percentage = '';

			// Update stored data
			if (this.shareModeEnabled) {
				this.sharedItemUpdate(uid, newFavouriteItem);
			} else {
				this.favouriteArray[flightIndex] = newFavouriteItem;
				this.localStorageWrite();
			}


			if (this.mode === 'list') {
				const html = this.resultsRenderRowHtml(resultsRender.response.flights[flightIndex], flightIndex, newFavouriteItem.filters);
				element.insertAdjacentHTML('afterend', html);

				// Animate the change
				element.dataset.status = 'removing';

				setTimeout(() => {
					element.nextElementSibling.dataset.status = 'clean';
				}, 0);

				setTimeout(() => {
					element.nextElementSibling.dataset.status = 'good';
					element.nextElementSibling.classList.remove('changing');
					element.remove();
				}, 550);
			} else if (this.mode === 'table') {
				this.renderTable();
			}
		};

		try {
			const reference = await initSearch();
			await updateSearch(reference);
			const response = await getRefreshed(reference);
			await handleRefreshedFlight(response);
		} catch (e) {
			import.meta.env.DEV && console.log('refreshListFlight: Error', e);
			breakRecord(l('fav.ui.refreshFailed'));
		}
	}

	/**
	 * Toggle share mode to use Redis instead of local storage
	 */
	sharedModeToggle () {
		this.shareModeEnabled = !this.shareModeEnabled;

		// use toggle to add or remove class
		document.body.classList.toggle('share-mode', this.shareModeEnabled);

		if (!this.shareModeEnabled) {
			this.sharedKeyDelete();
			window.location = window.location.origin + window.location.pathname;
		}
	}

	/**
	 * We store the ShareKey if we continue in the group favorite mode
	 * @returns {string|null}
	 */
	sharedKeyGet () {
		return storedItemRead('ShareKey', 'sessionStorage');
	}

	// We store the ShareKey if we continue in the group favorite mode
	/**
	 * @param {string} key
	 */
	sharedKeySet (key) {
		if (key) {
			storedItemCreate('ShareKey', key, 'sessionStorage');
		}
	}

	/**
	 * We remove the key when we are out of share mode
	 */
	sharedKeyDelete () {
		storedItemDelete('ShareKey', 'sessionStorage');
	}

	/**
	 * Store the sharename for display reasons
	 */
	sharedNameGet () {
		return storedItemRead('ShareName', 'sessionStorage');
	}

	/**
	 * We store the Sharename for display reasons
	 * @param {string} name
	 */
	sharedNameSet (name) {
		storedItemCreate('ShareName', name, 'sessionStorage');
	}

	/**
	 * Tries to get the shared flights from the server based on the shareKey, if successfull it will dispatch an event "sharedFetched"
	 */
	sharedListGet () {
		ConnectApi('Favorite', 'sharedListGet', [this.sharedKeyGet()]).then((response) => {
			EventDispatcher.dispatch('sharedFetched', response);
		}).catch((error) => {
			console.log(error);
		}).finally(() => {
			console.log('🛬 Shared flights fetched');
		});
	}

	sharedListCreate () {
		ConnectApi('Favorite', 'sharedListCreate', [storedItemRead('favorite')]).then((response) => {
			if (response.status === 'ok') {
				this.shareLink = window.location.origin + window.location.pathname + '?share=' + response.key;
				shareClick({ title: js_params.domain_title, url: this.shareLink }).catch((error) => {
					if (error.name === 'AbortError') {
						return;
					}
					copyToClipboard(this.shareLink).then((error) => {
						toaster.newToast(l('flightcompare.page.clipboard'), 'info', 'top');
					});
				});
			} else {
				// TODO: add dt
				toaster.newToast(l('Oh bother'), 'info', 'top');
			}
		}).catch(() => {
			toaster.newToast(l('Oh bother'), 'info', 'top');
		});
	}

	/**
	 * Saveds an array of flights to the server based on the shareKey, if successfull it will dispatch an event "sharedItemAdded"
	 * @param {array} flights
	 */
	sharedItemAdd (flights) {
		ConnectApi('Favorite', 'sharedItemAdd', [this.sharedKeyGet(), flights]).then((response) => {
			EventDispatcher.dispatch('sharedItemAdded', response);
		}).catch((error) => {
			console.log(error);
		}).finally(() => {
			console.log('🛫 Shared flights saved');
		});
	}

	/**
	 * Removes flights from the server based on the shareKey, if successfull it will dispatch an event "sharedRemoved"
	 * @param {array} flights
	 */
	sharedItemRemove (flights) {
		ConnectApi('Favorite', 'sharedItemRemove', [this.sharedKeyGet(), flights]).then((response) => {
			EventDispatcher.dispatch('sharedRemoved', response);
		}).catch((error) => {
			console.log(error);
		});
	}

	/**
	 * Updates flights on the server based on the shareKey, if successfull it will dispatch an event "sharedRefreshed"
	 * @param {array} flight
	 */
	sharedItemUpdate (oldUid, flight) {
		ConnectApi('Favorite', 'sharedItemUpdate', [this.sharedKeyGet(), oldUid, flight]).then((response) => {
			EventDispatcher.dispatch('sharedRefreshed', response);
		}).catch((error) => {
			console.log(error);
		});
	}

	/**
	 * Sets this.favouriteArray from the local storage
	 */
	localStorageRead () {
		if (!this.shareModeEnabled) {
			let lS = storedItemRead('favorite');

			if (lS?.length) {
				lS = lS.filter(localStoredList => localStoredList !== false);
			}

			if (lS) {
				this.favouriteArray = lS || [];
			} else {
				this.favouriteArray = [];
			}
		}
	}

	/**
	 * Pushes this.favouriteArray to the local storage
	 */
	localStorageWrite () {
		if (!this.shareModeEnabled) {
			storedItemCreate('favorite', this.favouriteArray);
		}
		console.log(this.favouriteArray);
	}

	favouritesGetFlights () {
		return ConnectApi('Favorite', 'getFlights', [this.favouriteArray]);
	}

	/**
	 * Process flights for table mode
	 */
	flightsProcessForTable () {
		this.processedFlights = [];

		const parsedFlights = resultsRender.response.flights.filter(f => f.trips.length <= 2).map((flight, index) => resultsRender.getFlightRenderData(flight, index, true, 'favourites:table'));

		let onlyOneWay = true;
		if (parsedFlights.length) {
			let minPrice = 9999999;

			const tripTypeTranslation = {
				oneway: l('searchBox.flightType.oneWayShort'),
				return: l('searchBox.flightType.twoWayShort'),
				openjaw: l('searchBox.flightType.openJawShort')
			};

			let longestOutboundSegmentLength = 0;
			let longestInboundSegmentLength = 0;


			this.processedFlights = parsedFlights.map((flight) => {
				let processedFlight = {};

				if (!flight?.ancient) {
					if (flight.trips[0].segment.length > longestOutboundSegmentLength) {
						longestOutboundSegmentLength = flight.trips[0].segment.length;
					}
					if (flight.trips.length && flight.trips.slice(-1)[0].segment.length > longestInboundSegmentLength) {
						longestInboundSegmentLength = flight.trips.slice(-1)[0].segment.length;
					}

					processedFlight = {
						index: flight.index,
						url: flight.agents[0].bookingUrl,
						agent: flight.agents[0].agent,
						from: flight.trips[0].segment[0].departureCity,
						to: flight.trips[0].segment.slice(-1)[0].arrivalCity,
						tripType: flight.tripType,
						extra: {
							price: flight.agents[0].price,
							tripType: tripTypeTranslation[flight.tripType],
							rebooking: !!flight.agent?.urlOptions?.flexTicket,
							baggage: !!flight.agent?.urlOptions?.baggageIncluded,
							cabinBaggage: !!flight.agent?.urlOptions?.cabinBaggageIncluded,
							climateCompensated: !!flight.agent?.urlOptions?.climateCompensated,
							travelTime: getTimeText(flight.traveltime),
							airlines: flight.airlines,
							carbonEmisionValue: flight.emission + 'kg',
							passengers: flight.favouriteMeta.passengers?.adults + (flight.favouriteMeta.passengers?.child?.count || 0),
							arrivalDate: flight.trips.slice(-1)[0].segment.slice(-1)[0].localizedArrivalDayShortString,
							arrivalTime: flight.trips[0].segment.slice(-1)[0].arrivalTime,
							sameDayArrival: true,
							waitingTimesOutbound: flight.trips[0].totalWaitingTime ? getTimeText(flight.trips[0].totalWaitingTime) : '',
							outboundDepartureDate: flight.trips[0].segment[0].localizedDepartureDayShortString,
							outboundDepartureTime: flight.trips[0].segment[0].departureTime,
							outboundArrivalTime: flight.trips[0].segment.slice(-1)[0].arrivalTime,
							outboundArrivalDate: flight.trips[0].segment.slice(-1)[0].localizedArrivalDayShortString,
							outboundDestinationAirport: flight.trips[0].segment.slice(-1)[0].arrivalAirport,
							waitingTimesInbound: flight.trips[1]?.totalWaitingTime ? getTimeText(flight.trips[1].totalWaitingTime) : '',
							outboundTrips: flight.trips[0].segment.map(segment => ({
								airport: segment.departureAirport,
								airline: resultsRender.response.airlineDetails[segment.airlineCode],
								cabin: cabinType(segment.cabin),
								waitTime: segment.waitTime
							}))
						},
						isAncient: false,
						isOld: flight.favouriteMeta.isOld,
						isPast: flight.favouriteMeta.isPast
					};

					// Remove inbound/return part if there is only one ways
					if (processedFlight.tripType !== 'oneway') {
						onlyOneWay = false;

						processedFlight.extra.inboundDepartureDate = flight.trips[1]?.segment[0]?.localizedDepartureDayShortString;
						processedFlight.extra.inboundDepartureTime = flight.trips[1]?.segment[0]?.departureTime;
						processedFlight.extra.inboundArrivalTime = flight.trips[1]?.segment.slice(-1)[0]?.arrivalTime;
						processedFlight.extra.inboundArrivalDate = flight.trips[1]?.segment.slice(-1)[0]?.localizedArrivalDayShortString;
						processedFlight.extra.inboundDestinationAirport = flight.trips?.[1]?.segment?.slice?.(-1)?.[0]?.arrivalAirport;
						processedFlight.extra.inboundTrips = flight.trips.length
							? flight.trips.slice(-1)[0].segment.map(segment => ({
								airport: segment.departureAirport,
								airline: resultsRender.response.airlineDetails[segment.airlineCode],
								cabin: cabinType(segment.cabin),
								waitTime: segment.waitTime
							}))
							: [];
					} else {
						processedFlight.extra.inboundDepartureDate = '';
						processedFlight.extra.inboundDepartureTime = '';
						processedFlight.extra.inboundArrivalTime = '';
						processedFlight.extra.inboundArrivalDate = '';
						processedFlight.extra.inboundDestinationAirport = '';
						processedFlight.extra.inboundTrips = flight.trips.length
							? flight.trips.slice(-1)[0].segment.map(segment => ({
								airport: '',
								airline: '',
								cabin: '',
								waitTime: ''
							}))
							: [];
					}

					// Count all segments in for all trips. If return trip then minus 2
					processedFlight.extra.stops = flight.trips.reduce((acc, trip) => acc + trip.segment.length, 0);
					processedFlight.extra.stops -= flight.tripType === 'return' ? 2 : 1;

					if (flight.tripType === 'return') {
						processedFlight.extra.waitingTimesInbound = flight.trips[1].totalWaitingTime ? getTimeText(flight.trips[1].totalWaitingTime) : '';
					}

					if (flight.agents[0].price < minPrice) {
						minPrice = flight.agents[0].price;
					}

					if (flight.trips[0].segment[0].localizedArrivalDayShortString !== flight.trips[0].segment.slice(-1)[0].localizedArrivalDayShortString) {
						processedFlight.extra.sameDayArrival = false;
					}

					const waitingTime = flight.trips.find(t => t.waitingTimeStatus !== 'ok') ? flight.trips.find(t => t.waitingTimeStatus !== 'ok').waitingTimeStatus : 'ok'; // check long and short waiting time

					switch (waitingTime) {
						case 'ok':
							processedFlight.extra.waitingTimes = l('favorite.tableMode.okWaitingTime');
							processedFlight.extra.waiting = 'ok';
							break;
						case 'short':
							processedFlight.extra.waitingTimes = l('favorite.tableMode.shortWaitingTime');
							processedFlight.extra.waiting = 'short';
							break;
						case 'long':
							processedFlight.extra.waitingTimes = l('favorite.tableMode.longWaitingTime');
							processedFlight.extra.waiting = 'long';
							break;
					}
				} else {
					// Ancient support
					if (new Date(flight.trips[0].date) > new Date()) {
						processedFlight = {
							index: flight.index,
							from: flight.trips[0].from[0].city,
							to: flight.trips[0].to[0].city,
							extra: {
								outboundDepartureDate: flight.trips[0].date,
								outboundArrivalDate: flight.trips[1]?.date
							},
							url: flight.id,
							isAncient: true
						};
					}
				}

				return Object.keys(processedFlight).length ? processedFlight : false;
			});

			this.processedFlights = this.processedFlights.filter(Boolean);

			favoriteSettings[0].min = minPrice;

			const outboundIndex = favoriteSettings.findIndex(setting => setting.id === 'outboundTrips');
			// Inject table columns for outbound and inbound leg information

			if (!favoriteSettings[outboundIndex].children.length) {
				for (let i = 0; i < longestOutboundSegmentLength; i++) {
					const legArray = {
						id: `outboundMeta`,
						key: 'index',
						class: 'accordion',
						index: i,
						text: lreplace('global.simplePhrases.outboundLeg', { number: i + 1 }),
						display: 'value',
						visible: true,
						children: [
							{
								id: `outboundAirport`,
								key: 'airport',
								text: l('Flygplats'),
								display: 'value',
								visible: true
							},
							{
								id: `outboundAirline`,
								key: 'airline',
								text: l('searchBox.climateComp.airline'),
								display: 'value',
								visible: true
							},
							{
								id: `outboundCabin`,
								key: 'cabin',
								text: l('searchBox.ticketType.label'),
								display: 'value',
								visible: true
							},
							{
								id: 'outboundWaitTime',
								key: 'waitTime',
								text: l('flightcompare.setting.waitingTimes'),
								display: 'value',
								visible: true
							}]
					};

					favoriteSettings[outboundIndex].children.push(legArray);
				}
			}

			const inboundIndex = favoriteSettings.findIndex(setting => setting.id === 'inboundTrips');
			// Inject table columns for outbound and inbound leg information

			if (inboundIndex !== -1 && !favoriteSettings[inboundIndex]?.children.length) {
				for (let i = 0; i < longestInboundSegmentLength; i++) {
					const legArray = {
						id: `inboundMeta`,
						key: 'index',
						class: 'accordion',
						index: i,
						text: lreplace('global.simplePhrases.inboundLeg', { number: i + 1 }),
						display: 'value',
						visible: true,
						children: [
							{
								id: `inboundAirport`,
								key: 'airport',
								text: l('Flygplats'),
								display: 'value',
								visible: true
							},
							{
								id: `inboundAirline`,
								key: 'airline',
								text: l('searchBox.climateComp.airline'),
								display: 'value',
								visible: true
							},
							{
								id: `inboundCabin`,
								key: 'cabin',
								text: l('searchBox.ticketType.label'),
								display: 'value',
								visible: true
							},
							{
								id: `inboundWaitTime`,
								key: 'waitTime',
								text: l('flightcompare.setting.waitingTimes'),
								display: 'value',
								visible: true
							}]
					};

					favoriteSettings[inboundIndex].children.push(legArray);
				}
			}
		}

		if (onlyOneWay) {
			const inboundSettings = ['waitingTimesInbound', 'inboundMeta', 'inboundDepartureDate', 'inboundDepartureTime', 'inboundArrivalDate', 'inboundArrivalTime', 'inboundTrips', 'inboundDestinationAirport'];
			favoriteSettings = favoriteSettings.filter(favSetting => !inboundSettings.includes(favSetting.id));
		}
	}

	/**
	//  * Manages what to render, if processing again and if there is any option
	 */
	async render () {
		if (this.isShowresultPage) {
			return;
		}

		if (this.mode === 'list') {
			await this.renderList();
		} else if (this.mode === 'table') {
			this.renderTable();
		}
		if (['list', 'table'].includes(this.mode)) {
			this.renderHeader();
		}
	}

	renderHeader (title = l('fav.ui.favTitle'), subtitle = '', content = '') {
		if (!subtitle) {
			if (this.shareModeEnabled) {
				title = lreplace('fav.title.shared', { sharelistName: this.sharedNameGet() });
				subtitle = l('fav.title.description');
				content = `<button class="abBtn abBtn--blueWhite share">${l('fav.ui.keepSharingBtn')} <span class="icon icon-share"></span></button>`;
			} else {
				subtitle = this.favouriteArray.length ? '' : l('flightcompare.page.addFlights');
			}
		}

		const element = gebi('favlist-title');
		element.classList.remove('fetching');
		element.innerHTML = `<h2 class="sectionIntro__title">${title}</h2><span class="favlist-subtitle">${subtitle} ${content}</span>`;
		if (qs('.favBtn.mode') && qs('.favBtn.share') && this.favouriteArray.length === 0) {
			qs('.favBtn.mode').disabled = true;
			qs('.favBtn.share').disabled = true;
		}
	}

	/**
	 * Render standard list
	 */
	async renderList () {
		if (this.favouriteArray?.length) {
			let arrayChanged = false;
			if (!Object.keys(resultsRender.response).length) {
				resultsRender.response = await this.favouritesGetFlights();
			}

			if (resultsRender.response?.flights?.length !== this.favouriteArray.length) {
				resultsRender.response = await this.favouritesGetFlights();
			}

			if (!resultsRender.response.flights.length) {
				if (gebi('favorite-table')) {
					gebi('favorite-table').remove();
				}

				this.favouriteArray = [];
				this.localStorageWrite();

				this.renderHeader(l('fav.error.noflights'), '', l('fav.error.description'));
				return;
			}

			// Force first flight to have an old search date
			if (import.meta.env.DEV && this.flightArray?.[0]) {
				resultsRender.response.flights[0].favouriteMeta.isOld = true;
			}

			// // Force second flight be a past search
			if (import.meta.env.DEV && this.flightArray?.[1]) {
				resultsRender.response.flights[1].favouriteMeta.isPast = true;
			}

			let html = '';
			const finalArray = this.favouriteArray;

			resultsRender.response.flights.forEach((flight, index) => {
				if (!flight) {
					finalArray[index] = false;
					arrayChanged = true;
				} else {
					if (flight.agents) {
						html += this.resultsRenderRowHtml(flight, index, flight.favouriteMeta.filters);
					} else if (flight.trips[0]) {
						// If there is no agents, probably is an old search and not saved

						// 1 if ancient and in flight in the past, we remove it
						if (new Date(flight.trips[0].date) <= new Date()) {
							if (this.favouriteArray.length > 1) {
								this.favouriteArray.splice(index, 1);
							} else {
								this.favouriteArray = [];
							}
							arrayChanged = true;
						} else { // 2 if only ancient, we render special snippet
							html += this.renderAncientRecord(flight, index);
						}
					}
				}
			});


			if (arrayChanged) {
				if (this.shareModeEnabled) {
					ConnectApi('Favorite', 'sharedListSet', this.sharedKeyGet(), this.favouriteArray);
					this.sharedListGet();
				} else {
					this.localStorageWrite();
				}
			}

			this.parentElement.innerHTML = html;
		} else {
			// Empty handling
			if (gebi('favorite-table')) {
				gebi('favorite-table').remove();
			}
		}
	}

	renderAncientRecord (flight, index) {
		return `<div class="abResult ancientResult" data-index="${index}">
			<div class="abResult__main">
				<div class="abResult__airlineAndMoreInfo">
					<span class="tripIndex">${index + 1}</span>
					<div class="tripDestinations">
						<span class="tripDestination">
							${flight.trips[0].from[0].city}
							${flight.returntrip ? `<span class='icon-switch'></span>` : `<span class="icon-arrow-right"></span>`}
							${flight.trips[0].to[0].city}
						</span>
						<span class="tripDate">
							${flight.trips[0].date}
							${flight.returntrip ? `<span class='icon-switch'></span>${flight.trips[1].date}` : ``}
						</span>
					</div>
					<span class="ancientText">${l('showresult.message.old.long.title')}</span>
				</div>
				<div class="tools">
					<a href="/redo/${flight.id}" class="otaLink trck_agent-supersaver trck_result1 trck_ota1 trck_otaPrimary" rel="nofollow noopener noreferrer">
						<div class="abBuyBtn abBuyBtn--main">
							<div class="abBuyBtn__previewPrice">
								<span class="abBuyBtn__previewPrice--text">${l('showresult.message.oldDate.searchAgain')}</span>
							</div>
						</div>
					</a>
					<span class="icon-delete" data-action="delete"></span>
				</div>
			</div>
		</div>`;
	}

	/**
	 * Render special mode, nitro table
	 */
	renderTable () {
		this.flightsProcessForTable();

		const favouriteSettingsFiltered = favoriteSettingsFilteredByTableItems(this.processedFlights);

		if (this.processedFlights.length) {
			this.parentElement.innerHTML = `
			${compareTable(favouriteSettingsFiltered)}
			${floatingCol(this.processedFlights?.[this.pinnedIndex], this.pinnedIndex, true, favouriteSettingsFiltered)}
			<div class="floating-cols">
				${this.processedFlights
					.map((flight, index) =>
						this.pinnedIndex !== index
							? floatingCol(flight, index, false, favouriteSettingsFiltered)
							: ''
					)
					.join('')
				}
			</div>`;
		} else {
			// Empty handling
			if (gebi('favorite-table')) {
				gebi('favorite-table').remove();
			}
		}
	}


	resultsRenderRowHtml (flight, index, filters) {
		resultsRender.searchParams.filters = filters;
		return resultsRender.getRowHtml(flight, index, {
			filteredByClimateCompensation: resultsRender.isClimateCompensatedFiltered,
			showEmissions: resultsRender.isEmissionSorting || resultsRender.isClimateCompensatedFiltered,
			filters: resultsRender.searchParams.filters,
			...(js_params.passengers > 1 ? { multiplePassengers: js_params.passengers } : {}),
			displayFavBtn: true,
			isFavoritePage: true
		});
	}


	/**
	 * Render and manage
	 * @param {string} type new|add|remove|destroy
	 */
	drawerManager (type = 'new') {
		if (this.mode === 'drawer' && !this.drawer) {
			if (this.shareModeEnabled) {
				this.drawer = new Drawer('favorite', l('fav.ui.toShared'), false, () => {
					window.location.href = this.pageUrl;
				});
			} else {
				this.drawer = new Drawer('favorite', l('fav.ui.toFav'), false, () => {
					window.location.href = this.pageUrl;
				});
			}
		}

		if (type === 'add' || type === 'remove') {
			if (this.shareModeEnabled) {
				this.drawer.updateDrawer(l('fav.ui.toShared'));
			} else {
				this.drawer.updateDrawer(l('fav.ui.toFav'));
			}
		} else if (type === 'destroy' && this.drawer) {
			this.drawer.destroyDrawer();
			this.drawer = false;
		}
	}
}
